// src/components/VisualInspectionsList.js
// Shared read-only "what's already been inspected" list — used on BakingCcpLivePage,
// BakingCcpVerifyPage, PrepCcpVerifyPage, and ProductionLog.js's detail modal, all fed by the same
// GET /sqf/checklist-logs?batchId=(&productId=) call. A batch can have several Visual Inspections
// (multiple spot-checks a day, and — for a multi-product Baking CCP run — inspections across
// several different products), so each row is independently expandable rather than a flat
// comma-separated list, and each shows exactly which product/operator/time it was for.
import React, { useState } from 'react';
import { useLanguage, t } from '../context/LanguageContext';

const displayName = (u) => (u?.firstName || u?.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u?.username || '—');

function InspectionRow({ log }) {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const inst = log.Instance || {};
  const templateName = inst.Template?.name || 'Inspection';
  const productName = inst.Product?.name || null;
  const lotCode = inst.Batch?.lotCode || null;
  const when = log.submittedAt || log.createdAt;
  const whenStr = when ? new Date(when).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // Pull the visual_inspection field's criteria out of Responses for the expanded summary — the
  // field type is schema-defined, so find whichever response actually parses as one.
  let criteria = [];
  for (const r of (log.Responses || [])) {
    try {
      const parsed = JSON.parse(r.valueText || 'null');
      if (parsed && Array.isArray(parsed.criteria)) { criteria = parsed.criteria; break; }
    } catch { /* not this one */ }
  }

  return (
    <div className="border-bottom py-1" style={{ fontSize: 12 }}>
      <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span className="text-muted" style={{ width: 12 }}>{open ? '▾' : '▸'}</span>
        <span>{log.hasDeviation ? '⚠️' : '✓'}</span>
        {productName && <span className="fw-semibold">{productName}</span>}
        {lotCode && <span className="text-muted">({lotCode})</span>}
        <span className="text-muted">— {whenStr}</span>
        <span className="text-muted">— {displayName(log.FilledBy)}</span>
      </div>
      {open && (
        <div className="ms-4 mt-1 mb-2">
          <div className="text-muted mb-1">{templateName}</div>
          {criteria.length > 0 && (
            <ul className="mb-1 ps-3">
              {criteria.map((c, i) => (
                <li key={i}>
                  {c.label}: <span className={`badge bg-${c.aiStatus === 'pass' ? 'success' : c.aiStatus === 'flag' || c.withinTolerance === false ? 'danger' : 'secondary'}`} style={{ fontSize: 9 }}>
                    {c.key === 'weight' && c.diffPercent != null ? `${c.actualValue}${c.targetUnit || ''} (${c.diffPercent}%)` : c.aiStatus}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <a href={`/sqf/logs/${log.id}`} target="_blank" rel="noopener noreferrer">
            {t('Open full record ↗', 'Abrir registro completo ↗', lang)}
          </a>
        </div>
      )}
    </div>
  );
}

export default function VisualInspectionsList({ inspections }) {
  const { lang } = useLanguage();
  if (!inspections || inspections.length === 0) return null;
  return (
    <div className="alert alert-light border mb-3 py-2" style={{ fontSize: 13 }}>
      <strong>📷 {t('Visual Inspections', 'Inspecciones Visuales', lang)} ({inspections.length})</strong>
      <div className="mt-1">
        {inspections.map(log => <InspectionRow key={log.id} log={log} />)}
      </div>
    </div>
  );
}
